import { useState, useEffect, useCallback } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface AuthProvider {
  id: number;
  name: string;
  displayName?: string;
  providerType: string;
  clientId: string;
  clientSecret: string;
  hasClientId: boolean;
  hasClientSecret: boolean;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthProviderFormData {
  name: string;
  displayName: string;
  providerType: string;
  clientId: string;
  clientSecret: string;
  isActive: boolean;
  description: string;
}

const providerTypes = [
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook (Meta)' },
  { value: 'instagram', label: 'Instagram (Meta)' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'github', label: 'GitHub' },
];

export function SocialProviders() {
  const { accessToken } = useAuthStore();
  const [providers, setProviders] = useState<AuthProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AuthProvider | null>(null);
  const [formData, setFormData] = useState<AuthProviderFormData>({
    name: '',
    displayName: '',
    providerType: 'google',
    clientId: '',
    clientSecret: '',
    isActive: false,
    description: '',
  });

  const fetchProviders = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/auth-providers', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch social login providers');
      const data = await response.json();
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load social login providers');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleOpenModal = (provider?: AuthProvider) => {
    if (provider) {
      setEditingProvider(provider);
      setFormData({
        name: provider.name,
        displayName: provider.displayName || '',
        providerType: provider.providerType,
        clientId: '',
        clientSecret: '',
        isActive: provider.isActive,
        description: provider.description || '',
      });
    } else {
      setEditingProvider(null);
      setFormData({
        name: 'google',
        displayName: 'Google',
        providerType: 'google',
        clientId: '',
        clientSecret: '',
        isActive: false,
        description: '',
      });
    }
    setShowModal(true);
    setError('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProvider(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(editingProvider?.id || 0);

    try {
      const url = editingProvider
        ? `/api/admin/auth-providers/${editingProvider.id}`
        : '/api/admin/auth-providers';
      const method = editingProvider ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save provider');
      }

      await fetchProviders();
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (provider: AuthProvider) => {
    setActionLoading(provider.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/auth-providers/${provider.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to toggle provider');
      }
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (provider: AuthProvider) => {
    if (!confirm(`"${provider.displayName || provider.name}" provider'ını silmek istediğinize emin misiniz?`)) {
      return;
    }
    setActionLoading(provider.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/auth-providers/${provider.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete provider');
      }
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete provider');
    } finally {
      setActionLoading(null);
    }
  };

  const typeLabel = (type: string) =>
    providerTypes.find((p) => p.value === type)?.label || type;

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Social Login</h1>
          <p>OAuth sağlayıcılarını yönetin (Client ID / Secret). Kullanıcı bağları Identity AspNetUserLogins tablosunda tutulur.</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
          + Yeni Provider Ekle
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Auth Providers ({providers.length})</h2>
        </div>

        {providers.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz social login provider tanımlanmamış.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenModal()}>
              İlk Provider&apos;ı Ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      <div className="provider-info">
                        <div className="provider-name-row">
                          <span className="provider-name">{provider.displayName || provider.name}</span>
                          {!provider.hasClientId && (
                            <span className="provider-missing-api-key-badge" title="Client ID tanımlı değil">
                              <ExclamationTriangleIcon aria-hidden="true" />
                              Client ID yok
                            </span>
                          )}
                        </div>
                        {provider.description && (
                          <span className="provider-description">{provider.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="provider-type-badge">{typeLabel(provider.providerType)}</span>
                    </td>
                    <td>
                      <button
                        className={`admin-toggle-btn ${provider.isActive ? 'active' : ''}`}
                        onClick={() => handleToggleActive(provider)}
                        disabled={actionLoading === provider.id}
                        title={provider.isActive ? 'Deaktif Et' : 'Aktif Et'}
                      >
                        {provider.isActive ? '✓ Aktif' : '✗ Pasif'}
                      </button>
                    </td>
                    <td>
                      <div className="admin-action-buttons">
                        <button
                          className="admin-edit-btn"
                          onClick={() => handleOpenModal(provider)}
                          disabled={actionLoading === provider.id}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          className="admin-delete-btn"
                          onClick={() => handleDelete(provider)}
                          disabled={actionLoading === provider.id}
                          title="Sil"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingProvider ? 'Provider Düzenle' : 'Yeni Provider'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Name (unique key)
                  <input
                    className="admin-form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="google"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Display Name
                  <input
                    className="admin-form-input"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="Google"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Provider Type
                  <select
                    className="admin-form-select"
                    value={formData.providerType}
                    onChange={(e) => setFormData({ ...formData, providerType: e.target.value })}
                  >
                    {providerTypes.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Client ID
                  <input
                    className="admin-form-input"
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    required={!editingProvider}
                    placeholder={editingProvider?.hasClientId ? 'Boş bırakırsanız mevcut değer korunur' : 'OAuth Client ID'}
                    autoComplete="off"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Client Secret
                  <input
                    className="admin-form-input"
                    type="password"
                    value={formData.clientSecret}
                    onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                    placeholder={
                      editingProvider?.hasClientSecret
                        ? 'Boş bırakırsanız mevcut değer korunur'
                        : 'Opsiyonel (GIS id_token için gerekmez)'
                    }
                    autoComplete="new-password"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Description
                  <textarea
                    className="admin-form-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Aktif (login sayfasında göster)
                </label>
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn" onClick={handleCloseModal}>
                  İptal
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={actionLoading !== null}
                >
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
